export type Environment =
  | { kind: 'production' }
  | { kind: 'staging' }
  | { kind: 'dev'; user: string; worktree?: string };

/** Opt-in learning is separate from cosmetic settings and scoped by the worker. */
export interface LearningSettings {
  learnFromFormEdits: boolean;
  saveMissingInformation: boolean;
}

export const DEFAULT_LEARNING_SETTINGS: Readonly<LearningSettings> = Object.freeze({
  learnFromFormEdits: false,
  saveMissingInformation: false
});

/** Per-operation limits shared with MaxLearningObservations/MaxLearningValueBytes in Go. */
export const MAX_LEARNING_OBSERVATIONS = 20;
export const MAX_LEARNING_VALUE_BYTES = 4096;
export const MAX_LEARNING_PLACEMENT_ANCHORS = 20;

export const LEARNING_ENTRY_STATUSES = [
  'HINT_LEARNED',
  'FACT_SAVED',
  'NEEDS_REVIEW',
  'CONFLICT',
  'IGNORED',
  'FAILED',
  'UNDONE'
] as const;
export type LearningEntryStatus = (typeof LEARNING_ENTRY_STATUSES)[number];

/** A worker-authorized receipt or proposal, never an instruction to write to the page. */
export interface LearningEntry {
  id: string;
  fieldLabel: string;
  value: string;
  entityLabel?: string;
  status: LearningEntryStatus;
  /** A saved fact may also have taught a hint, with one atomic Undo receipt. */
  hintLearned: boolean;
  message?: string;
  canApply: boolean;
  canUndo: boolean;
}

export interface LearningOperation {
  id: string;
  checkpointId?: string;
  bucketId: string;
  bucketRevision: number;
  entries: LearningEntry[];
  modelCalls: number;
  elapsedMs: number;
}

export const LEARNING_DELIVERY_STATUSES = [
  'QUEUED',
  'SAVING',
  'CONFIRMED',
  'NEEDS_REVIEW',
  'FAILED',
  'UNCONFIRMED',
  'CANCELLED',
  'EXPIRED'
] as const;

/** Delivery evidence does not fabricate a server receipt or its metrics. */
export interface LearningDelivery {
  operationId: string;
  checkpointId: string;
  bucketId: string;
  observationCount: number;
  status: (typeof LEARNING_DELIVERY_STATUSES)[number];
  message?: string;
}

export interface LearningSnapshot {
  active: boolean;
  operations: LearningOperation[];
  settings: LearningSettings;
  stagedCount?: number;
  deliveries?: LearningDelivery[];
}

/** Unknown future capabilities remain readable by older capable clients. */
export const BRIDGE_CAPABILITIES = {
  LEARNING_CHECKPOINTS: 'learning-checkpoints-v1',
  RETRY_ADVICE: 'retry-advice-v1',
  REVERT_LEARNING: 'revert-learning-v1',
  BUCKET_PROFILES: 'bucket-profiles-v1'
} as const;

export interface BridgeCapabilities {
  capabilities: string[];
}

/** Site identity is resolved by the worker, never supplied by the sidebar. */
export interface RetryAdviceSettings {
  enabled: boolean;
  siteEnabled: boolean;
  origin: string | null;
}

export type AuthState =
  | {
      status: 'checking' | 'signed-out' | 'expired';
      userDisplayName: null;
      agencyName: null;
    }
  | {
      status: 'signed-in';
      userDisplayName: string | null;
      agencyName: string | null;
    };

export type TabContext =
  | {
      status: 'unavailable';
      contextId: null;
      origin: null;
      title: null;
    }
  | {
      status: 'unsupported';
      contextId: string;
      origin: string | null;
      title: string | null;
    }
  | {
      status: 'ready';
      contextId: string;
      origin: string;
      title: string;
    };

// Each enum below is declared once as a const value list with its type
// derived, so the runtime validators and the extension consume the same
// source of truth instead of maintaining parallel copies.
export const SMART_FILL_BUCKET_ORIGINS = ['MANUAL', 'COPY', 'WORKSPACE'] as const;
export type SmartFillBucketOrigin = (typeof SMART_FILL_BUCKET_ORIGINS)[number];

export const SMART_FILL_BUCKET_VISIBILITIES = ['PRIVATE', 'PUBLIC'] as const;
export type SmartFillBucketVisibility = (typeof SMART_FILL_BUCKET_VISIBILITIES)[number];

export const SUPER_COPY_REVIEW_CONFIDENCE_THRESHOLD = 0.75;

/** Shared ceiling for human-facing Smart Fill bucket names. */
export const MAX_BUCKET_NAME_LENGTH = 256;

// These limits mirror pkg/service/api/smartfill/bucket_document.go. The server
// revalidates authoritatively; the client mirror exists to fail fast at the
// bridge boundary.
export const BUCKET_DOCUMENT_VERSION = 1;
export const MAX_DOCUMENT_DEPTH = 6;
export const MAX_DOCUMENT_ENTITIES = 200;
export const MAX_DOCUMENT_PROPERTIES = 1_000;
export const MAX_DOCUMENT_NAME_LENGTH = 80;
export const MAX_DOCUMENT_LABEL_LENGTH = 120;
/** Property values are capped in UTF-8 bytes, as the server caps them. */
export const MAX_DOCUMENT_VALUE_BYTES = 100 * 1024;

export const BUCKET_VALUE_KINDS = [
  'TEXT',
  'BOOLEAN',
  'NUMBER',
  'DATE',
  'PHONE',
  'EMAIL',
  'MONEY',
  'IDENTIFIER'
] as const;

/**
 * How a property's scalar value behaves during matching and formatting. A
 * behavioral hint, not a semantic vocabulary: two properties keyed
 * license_number under different drivers share a kind but never an identity.
 */
export type BucketValueKind = (typeof BUCKET_VALUE_KINDS)[number];

/** One scalar fact owned by exactly one entity. Values are always strings. */
export interface BucketProperty {
  id: string;
  /** Stable machine identity within the owning entity. */
  semanticKey: string;
  /** Mutable human-facing wording; not part of property identity. */
  displayLabel?: string;
  kind: BucketValueKind;
  value: string;
  /** Extraction reliability in (0, 1]; manual values are 1. Unset means 1. */
  sourceConfidence?: number;
}

/**
 * One record in the bucket tree. Repeated records are sibling children
 * sharing a relation, for example two person children under "drivers".
 */
export interface BucketEntity {
  id: string;
  /** Names this entity's slot under its parent; absent on the root. */
  relation?: string;
  type: string;
  role?: string;
  label?: string;
  properties?: BucketProperty[];
  children?: BucketEntity[];
}

/** One bucket's complete entity tree, as returned by the server. */
export interface BucketDocument {
  version: typeof BUCKET_DOCUMENT_VERSION;
  root: BucketEntity;
}

/**
 * Draft variants sent from the sidebar: node IDs are omitted for new nodes
 * (the server mints them) and preserved for nodes that keep their place.
 */
export interface BucketPropertyDraft extends Omit<BucketProperty, 'id'> {
  id?: string;
}

export interface BucketEntityDraft extends Omit<BucketEntity, 'id' | 'properties' | 'children'> {
  id?: string;
  properties?: BucketPropertyDraft[];
  children?: BucketEntityDraft[];
}

export interface BucketDocumentDraft {
  version: typeof BUCKET_DOCUMENT_VERSION;
  root: BucketEntityDraft;
}

export interface SmartFillBucket {
  id: string;
  operationId: string;
  name: string;
  visibility: SmartFillBucketVisibility;
  origin: SmartFillBucketOrigin;
  document: BucketDocument;
  revision: number;
  canEdit: boolean;
  /** Learning changed the bucket, so its earlier version can be restored. */
  hasLearnedChanges?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** A stored document that cannot safely be selected, edited, or used for Paste. */
export interface UnavailableBucket {
  unavailable: true;
  id: string;
  name: string;
}

export type BucketListEntry = SmartFillBucket | UnavailableBucket;

export const WORKSPACE_STATES = ['CREATED', 'IDLE', 'WAITING_FOR_INPUT', 'WORKING'] as const;
export type WorkspaceState = (typeof WORKSPACE_STATES)[number];

export interface Workspace {
  id: string;
  title: string;
  clientName: string | null;
  state: WorkspaceState;
  updatedAt: string;
}

export interface WorkspaceConversion {
  operationId: string;
  bucketIds: string[];
  completed: boolean;
}

/**
 * One fact a profile's carriers ask for, named the way bucket properties name
 * it: the owning entity's type, the relations from the root to it, and its key.
 */
export interface BucketProfileFact {
  ownerType: string;
  /** Relations from the bucket root joined by "/", such as "drivers"; empty on the root. */
  relationPath: string;
  semanticKey: string;
  label: string;
  kind: BucketValueKind;
  /** How many of the profile's carriers ask for the fact. */
  carrierCount: number;
}

/** A kind of bucket, such as personal auto, shaped by the questions its carriers ask. */
export interface BucketProfile {
  id: string;
  name: string;
  carriers: string[];
  /** Asked by the most carriers first. */
  facts: BucketProfileFact[];
}

export interface WorkspaceUsers {
  currentUserId: string;
  users: { id: string; name: string; email: string }[];
}

export interface FillSource {
  kind: 'BUCKET';
  bucketId: string;
  label: string;
}

/** Source identity belongs to the tab, not to whichever page is currently visible. */
export interface FillSelection {
  bucketId: string;
  scopeEntityId: string | null;
  revision: number;
}

export interface StateStamp {
  epoch: string;
  revision: number;
}

export interface StateRevisions {
  epoch: string;
  auth: number;
  tab: number;
  activeBucket: number;
  pageSession: number;
  superCopy: number;
}

export const BRIDGE_ERROR_CODES = [
  'INVALID_REQUEST',
  'NOT_SIGNED_IN',
  'AUTH_EXPIRED',
  'ACCESS_DENIED',
  'NOT_FOUND',
  'CONFLICT',
  'NO_ACTIVE_TAB',
  'UNSUPPORTED_PAGE',
  'NO_ACTIVE_BUCKET',
  'NO_FORM_FOUND',
  'SOURCE_TOO_LARGE',
  'SESSION_EXPIRED',
  'TAB_CHANGED',
  'TIMEOUT',
  'NETWORK',
  'INTERNAL'
] as const;
export type BridgeErrorCode = (typeof BRIDGE_ERROR_CODES)[number];

export interface BridgeError {
  code: BridgeErrorCode;
  message: string;
  retryable: boolean;
}

export const BRIDGE_WARNING_CODES = ['EVIDENCE_PENDING', 'COMPLETED_IN_BACKGROUND'] as const;
export type BridgeWarningCode = (typeof BRIDGE_WARNING_CODES)[number];

export interface BridgeWarning {
  code: BridgeWarningCode;
  message: string;
}

export const FILL_MODES = ['ONCE', 'CONTINUOUS'] as const;
export type FillMode = (typeof FILL_MODES)[number];

export const PAGE_SESSION_PHASES = [
  'idle',
  'extracting',
  'resolving',
  'filling',
  'waiting',
  'reviewing',
  'done',
  'error'
] as const;
export type PageSessionPhase = (typeof PAGE_SESSION_PHASES)[number];

export const PAGE_FIELD_STATES = ['CONFIDENT', 'REVIEW', 'EMPTY'] as const;
export type PageFieldState = (typeof PAGE_FIELD_STATES)[number];

export const PAGE_FIELD_WRITE_STATES = [
  'SKIPPED',
  'ALREADY_FILLED',
  'WRITTEN',
  'REJECTED'
] as const;
export type PageFieldWriteState = (typeof PAGE_FIELD_WRITE_STATES)[number];

export const SMART_FILL_DECISION_STATUSES = ['WRITE', 'SKIP', 'REVIEW'] as const;
export type SmartFillDecisionStatus = (typeof SMART_FILL_DECISION_STATUSES)[number];

export interface PageReviewRow {
  fieldId: string;
  label: string;
  value: string;
  confidence: number;
  state: PageFieldState;
  writeState: PageFieldWriteState;
  /** Value-free browser acceptance/cancellation code, separate from matching reason. */
  writeReason?: string;
  decisionStatus: SmartFillDecisionStatus;
  /**
   * Populated for REVIEW decisions, e.g. NO_MATCH or OPTION_MISMATCH. On a
   * WRITE, ASSUMED_OPTION or ASSUMED_DAY names an assumption to check.
   */
  reason: string | null;
  /** The bucket property the value came from, when one was matched. */
  propertyId: string | null;
  focused: boolean;
}

export interface PageSessionCounts {
  total: number;
  filled: number;
  review: number;
  empty: number;
}

export const CONTINUOUS_FILL_STATUSES = ['ACTIVE', 'WAITING', 'PAUSED', 'FINISHED'] as const;
export type ContinuousFillStatus = (typeof CONTINUOUS_FILL_STATUSES)[number];

export const CONTINUOUS_WAITING_REASONS = ['NEXT', 'NO_FIELDS'] as const;
export type ContinuousWaitingReason = (typeof CONTINUOUS_WAITING_REASONS)[number];

/** One earlier page of the session that still has flagged fields. */
export interface ContinuousReviewDebt {
  /** 1-based position of the page in the session's history. */
  step: number;
  reviewCount: number;
}

export interface ContinuousFillState {
  sessionId: string;
  status: ContinuousFillStatus;
  stepCount: number;
  cumulativeFilledCount: number;
  cumulativeReviewCount: number;
  waitingReason: ContinuousWaitingReason | null;
  reviewDebt: ContinuousReviewDebt[];
}

/**
 * What the last fill amounted to, judged from what was written. Anything but
 * SUCCESS and PARTIAL means nothing was written, and the kind says why, so the
 * panel never announces "Pasted 0 fields".
 */
export const FILL_OUTCOME_KINDS = [
  'SUCCESS',
  'PARTIAL',
  'NO_MATCH',
  'REVIEW_ONLY',
  'ALREADY_FILLED',
  'ENTITY_SELECTION_REQUIRED',
  'FAILED',
  'STALE'
] as const;
export type FillOutcomeKind = (typeof FILL_OUTCOME_KINDS)[number];

export interface FillOutcome {
  kind: FillOutcomeKind;
  /** Counts only, never field values. */
  summary: string;
}

export interface PageSessionSnapshot {
  sessionId: string | null;
  /** Opaque form cache identity, not an authorization token or field value. */
  formRevision?: string;
  phase: PageSessionPhase;
  mode: FillMode;
  source: FillSource | null;
  selection?: FillSelection;
  counts: PageSessionCounts;
  reviewRows: PageReviewRow[];
  continuous: ContinuousFillState | null;
  /** Null until a fill has completed on this page. */
  outcome: FillOutcome | null;
  error: BridgeError | null;
  warning: BridgeWarning | null;
}

export const LEARNED_RULE_ACTIONS = ['HINT', 'SKIP'] as const;
export type LearnedRuleAction = (typeof LEARNED_RULE_ACTIONS)[number];

/**
 * A correction rule the user taught for the form on the current page, as the
 * sidebar sees it: identity, what it does, and the page label of its field
 * when that field is part of the current fill. The form fingerprint and the
 * field key stay inside the extension; the background resolves both.
 */
export interface LearnedRule {
  id: string;
  fieldLabel: string | null;
  action: LearnedRuleAction;
  /** For HINT, what the field was taught to mean; null for SKIP. */
  meaning: string | null;
  updatedAt: string;
}

export const SUPER_COPY_BOUNDARY_KINDS = ['REGION', 'WHOLE_PAGE'] as const;
export type SuperCopyBoundaryKind = (typeof SUPER_COPY_BOUNDARY_KINDS)[number];

export const SUPER_COPY_GROUP_KINDS = [
  'BUSINESS',
  'CONTACT',
  'POLICY',
  'ADDRESS',
  'AGENCY',
  'CUSTOM'
] as const;
export type SuperCopyGroupKind = (typeof SUPER_COPY_GROUP_KINDS)[number];

export const SUPER_COPY_REVIEW_STATES = ['ACCEPTED', 'EDITED', 'CUSTOM', 'UNKNOWN'] as const;
export type SuperCopyReviewState = (typeof SUPER_COPY_REVIEW_STATES)[number];

export interface SuperCopyReviewSource {
  pageTitle: string;
  boundaryKind: SuperCopyBoundaryKind;
  boundaryLabel: string;
  truncated: boolean;
}

export interface SuperCopyReviewValue {
  id: string;
  semanticKey: string;
  label: string;
  extractedValue: string;
  value: string;
  confidence: number;
  reviewState: SuperCopyReviewState;
  accepted: boolean;
}

export const MAX_SUPER_COPY_CLIENTS = 20;
export const MAX_SUPER_COPY_GROUPS = 40;
export const MAX_SUPER_COPY_VALUES = 200;
export const SUPER_COPY_ENTITY_KINDS = [
  'person',
  'business',
  'vehicle',
  'property',
  'address',
  'policy',
  'agency',
  'section'
] as const;

/** Evidence-backed record meaning; absent/null is deliberately unclassified. */
export interface SuperCopyEntity {
  type: (typeof SUPER_COPY_ENTITY_KINDS)[number];
  role?: string | null;
  relation: string;
  parentGroupId?: string | null;
  confidence: number;
}

export interface SuperCopyReviewGroup {
  id: string;
  kind: SuperCopyGroupKind;
  label: string;
  entity?: SuperCopyEntity | null;
  values: SuperCopyReviewValue[];
}

export interface SuperCopyReviewClient {
  id: string;
  suggestedName: string;
  name: string;
  groups: SuperCopyReviewGroup[];
}

export interface SuperCopyReviewDraft {
  draftId: string;
  source: SuperCopyReviewSource;
  visibility: SmartFillBucketVisibility;
  clients: SuperCopyReviewClient[];
}

export const SUPER_COPY_PHASES = [
  'idle',
  'checking',
  'capturing',
  'extracting',
  'reviewing',
  'saving',
  'saved',
  'error'
] as const;
export type SuperCopyPhase = (typeof SUPER_COPY_PHASES)[number];

export const SUPER_COPY_OPERATION_STATES = [
  'READY',
  'SAVING',
  'BUCKET_SAVED',
  'EVIDENCE_PENDING',
  'COMPLETE',
  'FAILED'
] as const;
export type SuperCopyOperationState = (typeof SUPER_COPY_OPERATION_STATES)[number];

export interface SuperCopyOperationStatus {
  clientId: string;
  operationId: string;
  state: SuperCopyOperationState;
  bucketId: string | null;
  bucketName: string | null;
  error: BridgeError | null;
}

export interface SuperCopySnapshot {
  phase: SuperCopyPhase;
  completedDraftId: string | null;
  draft: SuperCopyReviewDraft | null;
  operations: SuperCopyOperationStatus[];
  error: BridgeError | null;
}

export interface SidebarSnapshot {
  auth: AuthState;
  tab: TabContext;
  activeBucket: FillSource | null;
  pageSession: PageSessionSnapshot;
  superCopy: SuperCopySnapshot;
}

export interface SmartFillUiPreferences {
  showPageButtons: boolean;
  showBottomBar: boolean;
  /**
   * Show failures the user cannot fix themselves. Absent means off; the
   * extension omits it while off so sidebars that predate it still parse.
   */
  showTechnicalProblems?: boolean;
}

export type SidebarDestination =
  | 'THIS_PAGE'
  | 'WORKSPACES'
  | { kind: 'GENERATE_WORKSPACE'; workspaceId: string };

export interface BootstrapSnapshot extends SidebarSnapshot {
  environment: Environment;
  stateRevisions: StateRevisions;
}

export type CaraDestination =
  | { kind: 'HOME' }
  | { kind: 'SMART_FILL' }
  | { kind: 'BUCKET'; bucketId: string }
  | { kind: 'WORKSPACE'; workspaceId: string };
